import { useRef } from 'react';

import { Box } from '@hivebrite/design-system/Box';

import { SimpleComment } from 'hb-react/frontoffice/apps/CommentsV2/components/SimpleComment';
import { Replies } from 'hb-react/frontoffice/apps/CommentsV2/components/v3/Replies';
import type { RepliesImperativeHandle } from 'hb-react/frontoffice/apps/CommentsV2/components/v3/Replies';
import { COMMENTABLE_TYPES } from 'hb-react/frontoffice/apps/CommentsV2/constants';
import { useCommentsContext } from 'hb-react/frontoffice/apps/CommentsV2/contexts/CommentsContext';
import type { Comment } from 'hb-react/frontoffice/apps/CommentsV2/types';

interface CommentWithRepliesProps {
  canDisplayEmojiPicker?: boolean;
  comment: Comment;
  currentUser: any;
  onDeleteComment: (attr: { id: number }) => void;
  onToggleLike: (attr: { commentId: number; newLikes: unknown }) => void;
  onUpdateComment: (attr: { comment: Comment }) => void;
  onVote: (attr: { commentId: number; state: unknown; score: unknown }) => void;
}

export const CommentWithReplies = ({
  canDisplayEmojiPicker,
  comment,
  currentUser,
  onDeleteComment,
  onToggleLike,
  onUpdateComment,
  onVote,
}: CommentWithRepliesProps) => {
  const repliesRef = useRef<RepliesImperativeHandle>(null);

  const { focusedCommentId, focusedReplyId, options } = useCommentsContext();

  const { id: commentId } = comment;
  const hasReplies = comment.has_replies || !!comment.replies?.total;
  const isFocused =
    !focusedReplyId && String(commentId) === String(focusedCommentId);

  const handleShowReplyForm = () => {
    repliesRef.current?.focusReplyForm();
  };

  const handleCreateReply = () => {
    onUpdateComment?.({
      comment: {
        ...comment,
        has_replies: true,
      },
    });
  };

  return (
    <Box flexDirection="column" data-testid="main-comment">
      <Box flexDirection="column" mb={4}>
        <SimpleComment
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          comment={comment}
          currentUser={currentUser}
          isFocused={isFocused}
          onDeleteComment={onDeleteComment}
          onReply={handleShowReplyForm}
          onToggleLike={onToggleLike}
          onUpdateComment={onUpdateComment}
          onVote={onVote}
          options={options}
        />
      </Box>

      <Replies
        commentableId={commentId}
        commentableType={COMMENTABLE_TYPES.COMMENT}
        currentUser={currentUser}
        hasReplies={hasReplies}
        onReplyCreated={handleCreateReply}
        repliesRef={repliesRef}
      />
    </Box>
  );
};
